<script setup lang="ts">
/**
 * Style quiz — full-screen layer (Figma Quizzes › Style quiz · desktop 3185:16166 /
 * mobile 3183:17532). Answers are saved at every step (R203): gender to Personal data,
 * brands / colors / styles to the style profile, heels to the heel preference, each
 * rating as a swipe. × asks first (16 Leave quiz) — both actions save, the modal only
 * says so.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  addBrands,
  addColors,
  addStyles,
  removeBrand,
  removeColor,
  removeStyle,
  updateHeelPreference,
} from '@/features/my-style/api/my-style'
import { STYLE_SURVEY_KEY, type StyleSurveySettings, type SurveyV2Config } from '@/features/survey/api/surveys'
import { hasSurveysApi } from '@/features/survey/flags'
import { submitTasteMatchingSwipe } from '@/features/taste/api/taste-matching'
import type { TasteMatchingCard } from '@/features/taste/types/taste-config'
import { useUpdatePersonalDataMutation } from '@/mutations/profile'
import { tasteDependentQueries } from '@/mutations/core/tasteDependentQueries'
import { queryCache } from '@/queries'
import {
  useAvailableBrandsQuery,
  useAvailableColorsQuery,
  useAvailableStylesQuery,
  useMyStyleQuery,
} from '@/queries/my-style'
import { useUserProfileDataQuery } from '@/queries/profile/useUserProfileDataQuery'
import { useSurveyDefinitionQuery } from '@/queries/survey'
import { PROFILE_MY_STYLE_ROUTE } from '@/router/routes'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizWelcome,
  settleFairQuizHistory,
} from '@/ui/patterns/FairQuiz'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairToggleItem } from '@/ui/primitives/FairToggleItem'
import FairProfileV2SelectionPicker from '@/widgets/profile/fair-v2/FairProfileV2SelectionPicker.vue'
import type { FairProfileV2PickerItem } from '@/widgets/profile/fair-v2/fair-profile-v2-my-style.data'
import { fetchStyleQuizCards, fetchStyleQuizStyleCodes } from '../api/style-quiz'
import { useStyleQuizStore } from '../store'
import {
  STYLE_QUIZ_HEEL_HEIGHT_API,
  diffStyleQuizIds,
  fromStyleQuizApiHeelHeight,
  nextStyleQuizStep,
  previousStyleQuizStep,
  resolveStyleQuizGender,
  styleQuizHeaderRight,
  styleQuizProgress,
  styleQuizSettingsFrom,
  styleQuizSteps,
  styleQuizSummaryList,
  styleQuizSwipe,
  toStyleQuizApiHeelFrequency,
  type StyleQuizGender,
  type StyleQuizHeelFrequency,
  type StyleQuizHeelHeight,
  type StyleQuizStep,
} from '../style-quiz.data'
import StyleQuizHeels from './StyleQuizHeels.vue'
import StyleQuizRate from './StyleQuizRate.vue'
import StyleQuizRateIntro, { type StyleQuizRateRow } from './StyleQuizRateIntro.vue'
import StyleQuizResult, { type StyleQuizSummaryRow } from './StyleQuizResult.vue'
import './style-quiz.css'

const store = useStyleQuizStore()
const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()

const profileQuery = useUserProfileDataQuery(computed(() => store.isOpen))
const myStyleQuery = useMyStyleQuery()
const stylesQuery = useAvailableStylesQuery()
const colorsQuery = useAvailableColorsQuery()
const brandsQuery = useAvailableBrandsQuery()
const personalDataMutation = useUpdatePersonalDataMutation()
/** BE-151 — `cards_per_style` / `min_styles` from the published style survey (the design's 5 / 2 until it loads). */
const styleSurveyQuery = useSurveyDefinitionQuery<SurveyV2Config>(STYLE_SURVEY_KEY, computed(() => store.isOpen))
const surveySettings = computed(() =>
  styleQuizSettingsFrom(styleSurveyQuery.data.value?.config?.settings as StyleSurveySettings | undefined),
)
const scale100 = hasSurveysApi('style_scale')

/* ── Flow ─────────────────────────────────────────────────────────────── */

const step = ref<StyleQuizStep>('welcome')
/** Personal data had no gender when the quiz opened (01 is asked only then). */
const askGender = ref(false)
const gender = ref<StyleQuizGender | null>(null)

const steps = computed(() => styleQuizSteps({ askGender: askGender.value, gender: gender.value }))

function goTo(next: StyleQuizStep) {
  step.value = next
  store.saveStep(next)
}

function goNext() {
  goTo(nextStyleQuizStep(steps.value, step.value))
}

function goBack() {
  goTo(previousStyleQuizStep(steps.value, step.value))
}

const saving = ref(false)

async function run(task: () => Promise<unknown>): Promise<boolean> {
  saving.value = true
  try {
    await task()
    return true
  } catch (error) {
    console.error('Style quiz: save failed', error)
    toast.show({ tone: 'warning', message: t('style_quiz.save_error') })
    return false
  } finally {
    saving.value = false
  }
}

const STYLE_QUERY_KEYS = [['my-style'], ['available-styles'], ['available-brands'], ['available-colors'], ['user-profile']]

function invalidateStyle() {
  STYLE_QUERY_KEYS.forEach((key) => queryCache.invalidate(key))
  queryCache.invalidate(tasteDependentQueries)
}

/* ── Saved profile ────────────────────────────────────────────────────── */

const myStyle = computed(() => myStyleQuery.data.value ?? null)
const savedStyleIds = computed(() => (myStyle.value?.styles ?? []).map((style) => style.id))
const savedColorIds = computed(() => (myStyle.value?.favoriteColors ?? []).map((color) => color.id))
const savedBrandIds = computed(() => (myStyle.value?.favoriteBrands ?? []).map((brand) => brand.id))

const brandPicks = ref<number[]>([])
const colorPicks = ref<number[]>([])
const stylePicks = ref<number[]>([])
const heelFrequency = ref<StyleQuizHeelFrequency | null>(null)
const heelHeight = ref<StyleQuizHeelHeight | null>(null)

function syncFromProfile() {
  const profile = profileQuery.data.value
  const known = resolveStyleQuizGender(profile?.user?.gender)
  askGender.value = !known
  gender.value = known
  const heels = profile?.taste?.heelPreference
  const apiFrequency = heels?.frequency as StyleQuizHeelFrequency | null | undefined
  heelFrequency.value = apiFrequency ?? null
  heelHeight.value = fromStyleQuizApiHeelHeight(heels?.height)
  brandPicks.value = [...savedBrandIds.value]
  colorPicks.value = [...savedColorIds.value]
  stylePicks.value = [...savedStyleIds.value]
}

const ready = ref(false)

/* ── Pickers ──────────────────────────────────────────────────────────── */

const brandItems = computed<FairProfileV2PickerItem[]>(() =>
  (brandsQuery.data.value ?? []).map((brand) => ({ id: brand.id, name: brand.name, image: brand.logo, popular: brand.popular })),
)
const colorItems = computed<FairProfileV2PickerItem[]>(() =>
  (colorsQuery.data.value ?? []).map((color) => ({ id: color.id, name: color.name, hex: color.hex, family: color.family })),
)
const styleItems = computed<FairProfileV2PickerItem[]>(() =>
  (stylesQuery.data.value ?? []).map((style) => ({
    id: style.id,
    name: style.name,
    image: style.image ?? undefined,
    imageSrcset: style.imageSrcset ?? undefined,
  })),
)

async function saveIds(
  saved: number[],
  next: number[],
  add: (ids: number[]) => Promise<unknown>,
  remove: (id: number) => Promise<unknown>,
) {
  const diff = diffStyleQuizIds(saved, next)
  if (!diff.add.length && !diff.remove.length) return
  await Promise.all([
    ...(diff.add.length ? [add(diff.add)] : []),
    ...diff.remove.map((id) => remove(id)),
  ])
  invalidateStyle()
  await myStyleQuery.refresh(true)
}

async function continueBrands() {
  const ok = await run(() =>
    saveIds(savedBrandIds.value, brandPicks.value, (ids) => addBrands({ brandIds: ids }), removeBrand),
  )
  if (ok) goNext()
}

async function continueColors() {
  const ok = await run(() =>
    saveIds(savedColorIds.value, colorPicks.value, (ids) => addColors({ colorIds: ids }), removeColor),
  )
  if (ok) goNext()
}

async function continueStyles() {
  const ok = await run(() =>
    saveIds(savedStyleIds.value, stylePicks.value, (ids) => addStyles({ styleIds: ids }), removeStyle),
  )
  if (!ok) return
  tickAllRateable()
  goNext()
}

/** Skip leaves the step as it was saved. */
function skip() {
  if (step.value === 'brands') brandPicks.value = [...savedBrandIds.value]
  if (step.value === 'colors') colorPicks.value = [...savedColorIds.value]
  goNext()
}

/* ── Gender ───────────────────────────────────────────────────────────── */

async function continueGender() {
  if (!gender.value) return
  const value = gender.value
  const ok = await run(() => personalDataMutation.mutateAsync({ gender: value }))
  if (ok) goNext()
}

/* ── Heels ────────────────────────────────────────────────────────────── */

function setHeelFrequency(value: StyleQuizHeelFrequency) {
  heelFrequency.value = value
  if (value === 'never') heelHeight.value = null
}

async function continueHeels() {
  const frequency = heelFrequency.value
  if (!frequency) return
  const height = frequency === 'never' || !heelHeight.value ? null : STYLE_QUIZ_HEEL_HEIGHT_API[heelHeight.value]
  const ok = await run(async () => {
    await updateHeelPreference({ frequency: toStyleQuizApiHeelFrequency(frequency, scale100), height })
    invalidateStyle()
  })
  if (ok) goNext()
}

/* ── Rate ─────────────────────────────────────────────────────────────── */

const rateChecked = ref<number[]>([])
const cards = ref<TasteMatchingCard[]>([])
const cardIndex = ref(0)
const cardsLoading = ref(false)
const cardsError = ref(false)
const ratedThisSession = ref(0)

/** The profile's styles; one with nothing left to rate stays listed but cannot be ticked. */
const rateRows = computed<StyleQuizRateRow[]>(() =>
  (myStyle.value?.styles ?? []).map((style) => ({
    id: style.id,
    name: style.name,
    image: style.image,
    imageSrcset: style.imageSrcset ?? null,
    outfits: Math.min(surveySettings.value.cardsPerStyle, Math.max(0, style.remainingSwipes ?? 0)),
  })),
)

const rateableIds = computed(() => rateRows.value.filter((row) => row.outfits > 0).map((row) => row.id))

/** What Rate intro shows ticked: the user's ticks among the rateable styles. */
const rateTicked = computed({
  get: () => rateChecked.value.filter((id) => rateableIds.value.includes(id)),
  set: (ids: number[]) => {
    rateChecked.value = ids
  },
})

function tickAllRateable() {
  rateChecked.value = [...rateableIds.value]
}

async function loadCards() {
  cardsLoading.value = true
  cardsError.value = false
  cards.value = []
  cardIndex.value = 0
  try {
    const codes = await fetchStyleQuizStyleCodes()
    const chosen = rateTicked.value
      .map((id) => codes.get(id))
      .filter((code): code is string => Boolean(code))
    const batches = await Promise.all(
      chosen.map((code) => fetchStyleQuizCards(code, surveySettings.value.cardsPerStyle)),
    )
    cards.value = batches.flatMap((batch) => batch.cards ?? [])
    if (!cards.value.length) goTo('result')
  } catch (error) {
    console.error('Style quiz: outfits did not load', error)
    cardsError.value = true
  } finally {
    cardsLoading.value = false
  }
}

function startRating() {
  goTo('rate')
  void loadCards()
}

const currentCard = computed(() => cards.value[cardIndex.value] ?? null)
const counter = computed(() =>
  t('style_quiz.rate.counter', {
    current: Math.min(cardIndex.value + 1, cards.value.length || 1),
    total: cards.value.length,
  }),
)

function rate(value: number) {
  const card = currentCard.value
  if (!card) return
  const { score, action } = styleQuizSwipe(value, scale100)
  submitTasteMatchingSwipe({ cardId: card.id, action, score })
    .then(() => {
      store.addRated()
      queryCache.invalidate(tasteDependentQueries)
    })
    .catch((error) => {
      console.error('Style quiz: rating not saved', error)
      toast.show({ tone: 'warning', message: t('style_quiz.save_error') })
    })
  ratedThisSession.value += 1
  if (cardIndex.value + 1 >= cards.value.length) {
    invalidateStyle()
    void myStyleQuery.refresh(true)
    goTo('result')
    return
  }
  cardIndex.value += 1
}

/* ── Result ───────────────────────────────────────────────────────────── */

const summaryRows = computed<StyleQuizSummaryRow[]>(() => {
  const heels = heelFrequency.value
    ? [
        t(`style_quiz.heels.frequency.${heelFrequency.value}`),
        heelHeight.value ? t(`style_quiz.heels.height.${heelHeight.value}`) : '',
      ]
        .filter(Boolean)
        .join(', ')
    : ''
  const rows: StyleQuizSummaryRow[] = [
    {
      key: 'styles',
      label: t('style_quiz.result.styles'),
      value: styleQuizSummaryList((myStyle.value?.styles ?? []).map((style) => style.name)),
    },
    {
      key: 'brands',
      label: t('style_quiz.result.brands'),
      value: styleQuizSummaryList((myStyle.value?.favoriteBrands ?? []).map((brand) => brand.name)),
    },
    {
      key: 'colors',
      label: t('style_quiz.result.colors'),
      value: styleQuizSummaryList((myStyle.value?.favoriteColors ?? []).map((color) => color.name)),
    },
  ]
  if (gender.value !== 'man') rows.push({ key: 'heels', label: t('style_quiz.result.heels'), value: heels })
  rows.push({ key: 'rated', label: t('style_quiz.result.rated'), value: String(store.ratedCount()) })
  return rows
})

async function openStyle() {
  store.close()
  await settleFairQuizHistory()
  await router.push(PROFILE_MY_STYLE_ROUTE)
}

function rateMore() {
  tickAllRateable()
  goTo('rate-intro')
}

/* ── Header + leaving ─────────────────────────────────────────────────── */

const headerRight = computed(() => styleQuizHeaderRight(step.value))
const progress = computed(() =>
  styleQuizProgress(steps.value, step.value, cardIndex.value, cards.value.length),
)

const leaveOpen = ref(false)

function requestClose() {
  // Nothing to lose on the first and last screens.
  if (step.value === 'welcome' || step.value === 'result') {
    store.close()
    return
  }
  leaveOpen.value = true
}

function exit() {
  leaveOpen.value = false
  store.close()
}

/* ── Open ─────────────────────────────────────────────────────────────── */

/** Every open re-reads the profile, then resumes (or goes to Rate intro). */
watch(
  () => store.session,
  async () => {
    ready.value = false
    ratedThisSession.value = 0
    await Promise.all([profileQuery.refresh(true), myStyleQuery.refresh(true), store.loadProgress()])
    syncFromProfile()
    ready.value = true
    if (store.entry.mode === 'rate') {
      if (store.entry.styleIds?.length) rateChecked.value = [...store.entry.styleIds]
      else tickAllRateable()
      step.value = 'rate-intro'
      return
    }
    const saved = store.savedStep()
    step.value = saved && steps.value.includes(saved) && saved !== 'rate' ? saved : 'welcome'
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="style-quiz"
    :class="`style-quiz--${step}`"
    :aria-label="t('style_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :counter="counter"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="skip"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('style_quiz.welcome.title')"
      :subtitle="t('style_quiz.welcome.subtitle')"
      :pills="[
        { label: t('style_quiz.welcome.pill_time') },
        { label: t('style_quiz.welcome.pill_saved'), star: false },
      ]"
      :continue-label="t('quiz.continue')"
      :continue-loading="!ready"
      @continue="goNext"
    />

    <FairQuizStep
      v-else-if="step === 'gender'"
      :title="t('style_quiz.gender.title')"
      :subtitle="t('style_quiz.gender.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!gender"
      :continue-loading="saving"
      @back="goBack"
      @continue="continueGender"
    >
      <div
        class="style-quiz__gender"
        role="radiogroup"
        :aria-label="t('style_quiz.gender.title')"
      >
        <FairToggleItem
          v-for="option in (['woman', 'man'] as const)"
          :key="option"
          :label="t(`style_quiz.gender.${option}`)"
          :selected="gender === option"
          stretch
          large
          @click="gender = option"
        />
      </div>
    </FairQuizStep>

    <FairQuizStep
      v-else-if="step === 'brands'"
      fill
      :title="t('style_quiz.brands.title')"
      :subtitle="t('style_quiz.brands.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-loading="saving"
      @back="goBack"
      @continue="continueBrands"
    >
      <FairProfileV2SelectionPicker
        v-model="brandPicks"
        kind="brands"
        :items="brandItems"
        :loading="brandsQuery.isLoading.value"
        :error="Boolean(brandsQuery.error.value)"
        @retry="brandsQuery.refresh(true)"
      />
    </FairQuizStep>

    <FairQuizStep
      v-else-if="step === 'heels'"
      :title="t('style_quiz.heels.title')"
      :subtitle="t('style_quiz.heels.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!heelFrequency"
      :continue-loading="saving"
      @back="goBack"
      @continue="continueHeels"
    >
      <StyleQuizHeels
        :frequency="heelFrequency"
        :height="heelHeight"
        @update:frequency="setHeelFrequency"
        @update:height="heelHeight = $event"
      />
    </FairQuizStep>

    <FairQuizStep
      v-else-if="step === 'colors'"
      fill
      :title="t('style_quiz.colors.title')"
      :subtitle="t('style_quiz.colors.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-loading="saving"
      @back="goBack"
      @continue="continueColors"
    >
      <FairProfileV2SelectionPicker
        v-model="colorPicks"
        kind="colors"
        :items="colorItems"
        :loading="colorsQuery.isLoading.value"
        :error="Boolean(colorsQuery.error.value)"
        @retry="colorsQuery.refresh(true)"
      />
    </FairQuizStep>

    <FairQuizStep
      v-else-if="step === 'styles'"
      fill
      class="style-quiz__styles"
      :title="t('style_quiz.styles.title')"
      :subtitle="t('style_quiz.styles.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="stylePicks.length < surveySettings.minStyles"
      :continue-loading="saving"
      @back="goBack"
      @continue="continueStyles"
    >
      <FairProfileV2SelectionPicker
        v-model="stylePicks"
        kind="styles"
        :items="styleItems"
        :loading="stylesQuery.isLoading.value"
        :error="Boolean(stylesQuery.error.value)"
        @retry="stylesQuery.refresh(true)"
      />
    </FairQuizStep>

    <StyleQuizRateIntro
      v-else-if="step === 'rate-intro'"
      v-model:checked="rateTicked"
      :rows="rateRows"
      :loading="!ready || myStyleQuery.isLoading.value"
      @back="goTo('styles')"
      @rate="startRating"
      @exit="store.close()"
    />

    <StyleQuizRate
      v-else-if="step === 'rate'"
      :image-url="currentCard?.imageUrl ?? null"
      :loading="cardsLoading"
      :error="cardsError"
      @rate="rate"
      @retry="loadCards"
    />

    <StyleQuizResult
      v-else-if="step === 'result'"
      :rows="summaryRows"
      @rate-more="rateMore"
      @open-style="openStyle"
    />

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('style_quiz.leave.title')"
      :text="t('style_quiz.leave.text')"
      :keep-label="t('style_quiz.leave.keep')"
      :exit-label="t('style_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
